import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useCounters } from '../../contexts/CounterContext';
import { Modal } from 'bootstrap';
import CreateCounterModal from '../modals/CreateCounterModal';
import { useToast } from '../../utils/toastUtils';
import { useMetaTags } from '../../hooks/useMetaTags';

function About() {
  const { t } = useTranslation(['about', 'translation']);
  const navigate = useNavigate();
  const { addCounter } = useCounters();
  const { toasts, showToast } = useToast();
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const modalRef = useRef<Modal | null>(null);
  
  // Use the custom hook for meta tags with about page specific values
  useMetaTags(
    t('metaTitle'),
    t('metaDescription')
  );

  const openCreateModal = () => {
    const modalEl = document.getElementById('createCounterModal');
    if (modalEl) {
      const modal = new Modal(modalEl);
      modalRef.current = modal;
      modal.show();
    }
  };

  const handleCreateCounter = async (name: string, isPrivate?: boolean, email?: string) => {
    try {
      setIsCreating(true);
      const newCounter = await addCounter(name, isPrivate, email);
      
      // Hide the modal
      if (modalRef.current) {
        modalRef.current.hide();
      }
      
      // Navigate to the new counter
      navigate(`/${newCounter.ownerUrl}`);
    } catch (err) {
      console.error('Failed to create counter:', err);
      setCreateError(t('errors.failedToCreate'));
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="about-page">
      {/* Header Section */}
      <div className="bg-primary text-white py-5">
        <div className="container">
          <div className="row justify-content-center text-center">
            <div className="col-lg-8">
              <h1 className="display-5 fw-bold mb-3">{t('title')}</h1>
              <p className="lead">{t('description')}</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="bg-body-tertiary py-5">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10">
              {/* Features Section */}
              <div className="mb-5">
                <h2 className="h3 fw-bold text-center mb-4">{t('features.title')}</h2>
                <div className="row g-4">
                  <div className="col-md-6">
                    <div className="d-flex bg-body-secondary p-3 rounded-4 shadow-sm h-100">
                      <div className="text-primary me-3 fs-3">
                        <i className="bi bi-ui-checks"></i>
                      </div>
                      <div>
                        <h3 className="h5 fw-bold">{t('features.customButtons')}</h3>
                        <p className="small text-body-secondary mb-0">{t('features.customButtonsDesc')}</p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="d-flex bg-body-secondary p-3 rounded-4 shadow-sm h-100">
                      <div className="text-primary me-3 fs-3">
                        <i className="bi bi-bar-chart"></i>
                      </div>
                      <div>
                        <h3 className="h5 fw-bold">{t('features.statistics')}</h3>
                        <p className="small text-body-secondary mb-0">{t('features.statisticsDesc')}</p>
                      </div>
                    </div>
                  </div>
                  {/* New Feature: Paid buttons using tokens you can buy */}
                  <div className="col-md-6">
                    <div className="d-flex bg-body-secondary p-3 rounded-4 shadow-sm h-100">
                      <div className="text-primary me-3 fs-3">
                        <i className="bi bi-currency-dollar"></i>
                      </div>
                      <div>
                        <h3 className="h5 fw-bold">{t('features.paidButtons')}</h3>
                        <p className="small text-body-secondary mb-0">{t('features.paidButtonsDesc')}</p>
                      </div>
                    </div>
                  </div>
                  {/* New Feature: Make money by creating paid buttons and withdraw your earnings */}
                  <div className="col-md-6">
                    <div className="d-flex bg-body-secondary p-3 rounded-4 shadow-sm h-100">
                      <div className="text-primary me-3 fs-3">
                        <i className="bi bi-wallet2"></i>
                      </div>
                      <div>
                        <h3 className="h5 fw-bold">{t('features.earnMoney')}</h3>
                        <p className="small text-body-secondary mb-0">{t('features.earnMoneyDesc')}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Use Cases Section */}
              <div className="mb-5">
                <h2 className="h3 fw-bold text-center mb-4">{t('useCases.title')}</h2>
                <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-3 g-4">
                  <div className="col">
                    <div className="card h-100 border-0 shadow-sm rounded-4 bg-body-secondary">
                      <div className="card-body p-4">
                        <div className="d-inline-flex align-items-center justify-content-center bg-primary-subtle text-primary-emphasis p-3 rounded-3 mb-3">
                          <i className="bi bi-trophy-fill fs-3"></i>
                        </div>
                        <h4 className="h5 card-title fw-bold">{t('useCases.clickCompetitions')}</h4>
                        <p className="card-text text-body-secondary small">{t('useCases.clickCompetitionsDesc')}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="col">
                    <div className="card h-100 border-0 shadow-sm rounded-4 bg-body-secondary">
                      <div className="card-body p-4">
                        <div className="d-inline-flex align-items-center justify-content-center bg-primary-subtle text-primary-emphasis p-3 rounded-3 mb-3">
                          <i className="bi bi-people-fill fs-3"></i>
                        </div>
                        <h4 className="h5 card-title fw-bold">{t('useCases.teamDecision')}</h4>
                        <p className="card-text text-body-secondary small">{t('useCases.teamDecisionDesc')}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="col">
                    <div className="card h-100 border-0 shadow-sm rounded-4 bg-body-secondary">
                      <div className="card-body p-4">
                        <div className="d-inline-flex align-items-center justify-content-center bg-primary-subtle text-primary-emphasis p-3 rounded-3 mb-3">
                          <i className="bi bi-calendar-check-fill fs-3"></i>
                        </div>
                        <h4 className="h5 card-title fw-bold">{t('useCases.eventPlanning')}</h4>
                        <p className="card-text text-body-secondary small">{t('useCases.eventPlanningDesc')}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* FAQ Section */}
      <div className="bg-body-tertiary py-5">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <div className="mb-5">
                <h2 className="h3 fw-bold text-center mb-4">{t('faq.title')}</h2>
                <div className="accordion rounded-4 shadow-sm overflow-hidden" id="faqAccordion">
                  <div className="accordion-item border-0">
                    <h3 className="accordion-header">
                      <button className="accordion-button collapsed bg-body-secondary fw-medium" type="button" data-bs-toggle="collapse" data-bs-target="#faq1" aria-expanded="false" aria-controls="faq1">
                        {t('faq.q1')}
                      </button>
                    </h3>
                    <div id="faq1" className="accordion-collapse collapse" data-bs-parent="#faqAccordion">
                      <div className="accordion-body bg-body-secondary">
                        <p className="mb-0 text-body-secondary">{t('faq.a1')}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="accordion-item border-0">
                    <h3 className="accordion-header">
                      <button className="accordion-button collapsed bg-body-secondary fw-medium" type="button" data-bs-toggle="collapse" data-bs-target="#faq2" aria-expanded="false" aria-controls="faq2">
                        {t('faq.q2')}
                      </button>
                    </h3>
                    <div id="faq2" className="accordion-collapse collapse" data-bs-parent="#faqAccordion">
                      <div className="accordion-body bg-body-secondary">
                        <p className="mb-0 text-body-secondary">{t('faq.a2')}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="accordion-item border-0">
                    <h3 className="accordion-header">
                      <button className="accordion-button collapsed bg-body-secondary fw-medium" type="button" data-bs-toggle="collapse" data-bs-target="#faq3" aria-expanded="false" aria-controls="faq3">
                        {t('faq.q3')}
                      </button>
                    </h3>
                    <div id="faq3" className="accordion-collapse collapse" data-bs-parent="#faqAccordion">
                      <div className="accordion-body bg-body-secondary">
                        <p className="mb-0 text-body-secondary">{t('faq.a3')}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="accordion-item border-0">
                    <h3 className="accordion-header">
                      <button className="accordion-button collapsed bg-body-secondary fw-medium" type="button" data-bs-toggle="collapse" data-bs-target="#faq4" aria-expanded="false" aria-controls="faq4">
                        {t('faq.q4')}
                      </button>
                    </h3>
                    <div id="faq4" className="accordion-collapse collapse" data-bs-parent="#faqAccordion">
                      <div className="accordion-body bg-body-secondary">
                        <p className="mb-0 text-body-secondary">{t('faq.a4')}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="accordion-item border-0">
                    <h3 className="accordion-header">
                      <button className="accordion-button collapsed bg-body-secondary fw-medium" type="button" data-bs-toggle="collapse" data-bs-target="#faq5" aria-expanded="false" aria-controls="faq5">
                        {t('faq.q5')}
                      </button>
                    </h3>
                    <div id="faq5" className="accordion-collapse collapse" data-bs-parent="#faqAccordion">
                      <div className="accordion-body bg-body-secondary">
                        <p className="mb-0 text-body-secondary">{t('faq.a5')}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="accordion-item border-0">
                    <h3 className="accordion-header">
                      <button className="accordion-button collapsed bg-body-secondary fw-medium" type="button" data-bs-toggle="collapse" data-bs-target="#faq6" aria-expanded="false" aria-controls="faq6">
                        {t('faq.q6')}
                      </button>
                    </h3>
                    <div id="faq6" className="accordion-collapse collapse" data-bs-parent="#faqAccordion">
                      <div className="accordion-body bg-body-secondary">
                        <p className="mb-0 text-body-secondary">{t('faq.a6')}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* CTA Section */}
      <div className="bg-primary text-white py-5">
        <div className="container py-2">
          <div className="row justify-content-center text-center">
            <div className="col-lg-6">
              <h2 className="fw-bold mb-4">{t('cta.title')}</h2>
              <p className="mb-4">{t('cta.description')}</p>
              <button
                className="btn btn-light btn-lg px-5 py-3 fw-medium shadow"
                onClick={openCreateModal}
              >
                <i className="bi bi-plus-circle me-2"></i>
                {t('createNewCounter')}
              </button>
              <p className="mt-3 small text-white-50">{t('quickStart')}</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Render the create counter modal */}
      <CreateCounterModal
        isCreating={isCreating}
        onCreateCounter={handleCreateCounter}
      />
    </div>
  );
}

export default About; 